/* eslint-disable camelcase */
import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Field, FormApi } from "react-final-form"
import styled from "styled-components"

import { resolveValidations } from "../../../../../../../sagas/configuratorSaga/validations"
import { getInputdValidationError } from "../../../../../../../sagas/configuratorSaga/helpers/utils"
import FieldError from "../../../../../../../components/FieldError"
import { FieldContainer, InputText, InputContainer } from "../styles"
import FieldExampleLink from "../../../FieldExampleLink"
import { ControlProps } from "../../types"
import FieldLabel from "../../../FieldLabel"

const Textarea = styled.textarea`
  box-sizing: border-box;
  border-radius: 4px;
  border: ${({ invalid }) =>
    invalid ? "1px solid rgb(199, 52, 52)" : "1px solid rgba(189, 197, 209, 0.5)"};
  outline: none;
  padding: 8px 16px;
  &:focus {
    border: 1px solid rgb(12, 125, 132);
    box-shadow: rgb(12, 125, 132) 0px 0px 3px;
  }
`

/**
 * Multi language textarea input
 *
 * This input will listen for changes to it's
 * "language_source" sibling input, a control in
 * the same group of type "language", it will use
 * the language value of this control to switch
 * it's contents to the right language.
 *
 * @param form
 * @param control
 * @returns {JSX.Element}
 * @constructor
 */
const TextArea = ({ form, control, showErrors }) => {
  const {
    name,
    type,
    language_source,
    value,
    constrains,
    suppressFeedback,
    separator,
    example,
    defaultValue,
    placeholder,
    required,
    isURL,
    container_styles,
    props: controlProps,
    hidden
  } = control
  const { disabled } = controlProps || {}
  const languageState = language_source ? form.getFieldState(language_source) : null
  const [languageListener, setLanguageListener] = useState(null)
  const [language, setLanguage] = useState(languageState ? languageState.value : null)

  useEffect(() => {
    if (language_source) {
      setLanguageListener(
        form.subscribe(
          ({ values }) => {
            if (values[language_source] !== language) {
              setLanguage(values[language_source])
              form.change(name, control[`value_${values[language_source]}`])
            }
          },
          { values: true }
        )
      )
    }
    return () => languageListener && languageListener()
  })

  const geDefaultValue = () => {
    if (language_source) {
      const valueKey = `value_${language || "en_US"}`

      if (valueKey in control && control[valueKey]) {
        return control[valueKey]
      }

      if (defaultValue && language && language in defaultValue) {
        return defaultValue[valueKey]
      }
    }

    return value
  }

  const errorProp = (input, meta) =>
    (showErrors || meta.touched) &&
    getInputdValidationError(input.value, control.label, required, isURL)

  const hasExampleLink = !disabled && example !== null && typeof example === "object"

  return (
    <Field
      name={name}
      type={type}
      initialValue={geDefaultValue()}
      validate={resolveValidations(constrains)}
    >
      {({ input, meta }) => (
        <FieldContainer
          container_styles={container_styles}
          separator={separator}
          hasExampleLink={hasExampleLink}
          hidden={hidden}
        >
          <FieldLabel control={control} />
          <InputContainer>
            {!disabled && (
              <Textarea
                {...(controlProps || {})}
                {...input}
                disabled={disabled}
                placeholder={placeholder}
                onChange={(event) => input.onChange(event.target.value || null)}
                invalid={!!errorProp(input, meta)}
              />
            )}
            {disabled && <InputText>{input.value || geDefaultValue() || "N/A"}</InputText>}
          </InputContainer>
          {!suppressFeedback && !disabled && (
            <FieldError
              touched
              error={errorProp(input, meta)}
              customStyles={{ marginBottom: "-20px" }}
            />
          )}
          {hasExampleLink && <FieldExampleLink control={control} />}
        </FieldContainer>
      )}
    </Field>
  )
}

TextArea.propTypes = {
  form: PropTypes.instanceOf(FormApi).isRequired,
  control: ControlProps.isRequired,
  validations: PropTypes.arrayOf(PropTypes.func),
  showErrors: PropTypes.bool
}

TextArea.defaultProps = {
  validations: [],
  showErrors: false
}

export default TextArea
